export const ACCESS_RESTRICTION_UPDATE_EVENT = "ACCESS_RESTRICTION_UPDATE";

export const ACCESS_RESTRICTION_CATEGORY = {
    MODULE_PERMISSION: "MODULE_PERMISSION",
    SCOPE_MISSING: "SCOPE_MISSING",
    EDITION_PLAN: "EDITION_PLAN",
    ACCOUNT_DISABLED: "ACCOUNT_DISABLED",
} as const;

export type AccessRestrictionCategory =
    (typeof ACCESS_RESTRICTION_CATEGORY)[keyof typeof ACCESS_RESTRICTION_CATEGORY];

export interface AccessRestrictionModule {
    label?: string;
    key?: string;
}

/** One banner item as Osync-AWS `AccessRestrictionPresenter` sends it, one per incident row. */
export interface AccessRestrictionItem {
    serviceId?: string;
    integId?: string;
    name?: string;
    category?: AccessRestrictionCategory;
    modules?: AccessRestrictionModule[];
    dismissible?: boolean;
    restrictionIds?: string[];
}

export interface AccessRestrictionState {
    accessRestrictionItems?: AccessRestrictionItem[];
    dismissedAccessRestrictionIds?: string[];
}

export interface AccessRestrictionUpdate {
    serviceId?: string;
    items?: unknown[];
}

// Matches AccessIssue.MODULE.ALL_MODULES on the server.
const ALL_MODULES_KEY = "*";
const ACCESS_RESTRICTION_DEFAULT_MODULES_TEXT = "all modules";
const ACCESS_RESTRICTION_DEFAULT_SERVICE_NAME = "CRM";

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function uniqueStrings(values: string[]): string[] {
    return [...new Set(values?.filter(Boolean))];
}

function joinLabels(labels: string[]): string {
    if (labels.length <= 1) {
        return labels?.[0] || "";
    }
    return `${labels.slice(0, -1).join(", ")} and ${labels?.[labels.length - 1]}`;
}

function capitalizeFirst(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function moduleIdentity(module?: AccessRestrictionModule | null): string {
    return String(module?.key || module?.label || "")
        .trim()
        .toLowerCase();
}

export function restrictionIdsOf(item?: AccessRestrictionItem | null): string[] {
    return item?.restrictionIds ?? [];
}

export function visibleAccessRestrictionItems(
    items?: AccessRestrictionItem[] | null,
    dismissedIds?: string[] | null
): AccessRestrictionItem[] {
    const hidden = new Set(dismissedIds ?? []);
    return (items ?? []).filter((item) => {
        const ids = restrictionIdsOf(item);
        return !ids?.length || ids?.some((id) => !hidden.has(id));
    });
}

export function accessRestrictionBannerCopy(items?: AccessRestrictionItem[] | null): { title: string; message: string } {
    const list = items ?? [];
    const serviceName =
        joinLabels(uniqueStrings(list.map((item) => String(item?.name || "").trim()))) ||
        ACCESS_RESTRICTION_DEFAULT_SERVICE_NAME;
    const modules = list.flatMap((item) => item?.modules ?? []);
    const moduleLabels = uniqueStrings(modules.map((module) => String(module?.label || "").trim().toLowerCase())).map(
        capitalizeFirst
    );
    const allModules = modules.some((module) => module?.key === ALL_MODULES_KEY);
    const modulesText =
        allModules || !moduleLabels?.length ? ACCESS_RESTRICTION_DEFAULT_MODULES_TEXT : joinLabels(moduleLabels);
    return {
        title: "Permissions Required",
        message: `HelloSend needs access to ${modulesText} in ${serviceName}. Please contact your admin to grant the required permissions.`,
    };
}

export function accessRestrictionCanDismiss(item?: AccessRestrictionItem | null): boolean {
    return item?.dismissible === true && Boolean(item?.integId) && restrictionIdsOf(item)?.length > 0;
}

export function withoutAccessRestrictionIds<T extends AccessRestrictionState>(
    previous: T | null | undefined,
    dismissedIds: string[]
): T {
    const dismissed = new Set(dismissedIds);
    return {
        ...(previous ?? {}),
        accessRestrictionItems: (previous?.accessRestrictionItems ?? []).filter(
            (item) => !restrictionIdsOf(item)?.some((id) => dismissed.has(id))
        ),
        // A push queued before the dismiss can still arrive after it; this keeps those rows hidden.
        dismissedAccessRestrictionIds: uniqueStrings([...(previous?.dismissedAccessRestrictionIds ?? []), ...dismissedIds]),
    } as T;
}

export function readAccessRestrictionUpdate(data: unknown): AccessRestrictionUpdate | null {
    const root = isRecord(data) ? data : null;
    let payload: unknown = root?.data ?? data;
    for (let depth = 0; depth < 2 && typeof payload === "string"; depth += 1) {
        try {
            const parsed: unknown = JSON.parse(payload);
            payload = parsed;
        } catch {
            return null;
        }
    }
    if (!isRecord(payload) || !Array.isArray(payload?.items)) {
        return null;
    }
    const serviceId = typeof payload?.serviceId === "string" ? payload.serviceId : undefined;
    return { serviceId, items: payload.items };
}

/** The server sends one item per incident row; the banner shows one per service and integration. */
export function collapseAccessRestrictionItems(items?: unknown): AccessRestrictionItem[] {
    const groups = new Map<string, AccessRestrictionItem>();
    (Array.isArray(items) ? items : []).filter(isRecord).forEach((entry) => {
        const item = entry as AccessRestrictionItem;
        const key = `${item?.serviceId ?? ""}:${item?.integId ?? ""}`;
        const current = groups.get(key);
        if (!current) {
            groups.set(key, item);
            return;
        }
        const modules = [...(current?.modules ?? []), ...(item?.modules ?? [])];
        groups.set(key, {
            ...current,
            ...item,
            category: item?.category || current?.category,
            // Dismiss sends every id in the group, and the server refuses the whole call if any one is not allowed.
            dismissible: current?.dismissible === true && item?.dismissible === true,
            modules: modules.filter(
                (module, index) => modules.findIndex((other) => moduleIdentity(other) === moduleIdentity(module)) === index
            ),
            restrictionIds: uniqueStrings([...restrictionIdsOf(current), ...restrictionIdsOf(item)]),
        });
    });
    return [...groups.values()];
}

export function applyAccessRestrictionUpdate(
    items: AccessRestrictionItem[] | null | undefined,
    payload: AccessRestrictionUpdate | null
): AccessRestrictionItem[] {
    const serviceId = payload?.serviceId;
    if (!serviceId) {
        return items ?? [];
    }
    return [
        ...(items ?? []).filter((item) => item?.serviceId !== serviceId),
        ...collapseAccessRestrictionItems(payload?.items),
    ];
}
